import React, { useRef, useEffect, useState } from 'react';

const REVIEWS = [
  { initials: 'RS', name: 'Rahul S.', role: 'Fresher, Mumbai', stars: 5,
    text: 'Finished my resume in 8 minutes. Got shortlisted at TCS in the same week. This thing is insane.' },
  { initials: 'PM', name: 'Priya M.', role: 'MBA Grad, Pune', stars: 5,
    text: 'Switching from BPO to HR felt impossible. The AI somehow framed 3 years of "random experience" into a clean HR profile.' },
  { initials: 'AK', name: 'Arjun K.', role: 'B.Tech CSE, Bangalore', stars: 5,
    text: 'Zero internships. Still got a professional resume. The AI added a projects section that actually looks good.' },
  { initials: 'SK', name: 'Sneha K.', role: 'Product Manager, Hyderabad', stars: 5,
    text: 'The ATS score feature is genuinely useful. Tweaked twice and callbacks went from almost zero to multiple per week.' },
  { initials: 'VN', name: 'Vikram N.', role: 'Backend Dev, Delhi', stars: 5,
    text: "10 minutes. That's how long it took. No cap." },
  { initials: 'DM', name: 'Deepa M.', role: 'Data Analyst, Chennai', stars: 5,
    text: 'The chat is so natural. Felt like texting a smart friend who happens to know exactly how resumes should be written.' },
  { initials: 'RT', name: 'Rohan T.', role: 'DevOps Eng, Kolkata', stars: 5,
    text: 'Templates look clean without being over-designed. Exactly the vibe product companies want.' },
  { initials: 'NA', name: 'Nisha A.', role: 'Frontend Dev, Bangalore', stars: 5,
    text: "Used three paid tools before this. None of them came close. Free tier was enough for everything I needed." },
];

// More scattered positions with wider spread
const POSITIONS = [
  { top: '0%',   left: '0%',  rotate: '-4deg',   delay: '0s' },
  { top: '2%',   left: '35%', rotate: '2deg',    delay: '0.12s' },
  { top: '0%',   left: '66%', rotate: '-1.5deg', delay: '0.06s' },
  { top: '36%',  left: '3%',  rotate: '3deg',    delay: '0.18s' },
  { top: '38%',  left: '34%', rotate: '-3.5deg', delay: '0.24s' },
  { top: '34%',  left: '67%', rotate: '2.5deg',  delay: '0.3s' },
  { top: '72%',  left: '8%',  rotate: '-2deg',   delay: '0.15s' },
  { top: '70%',  left: '52%', rotate: '4deg',    delay: '0.21s' },
];

function ReviewCard({ review, style, className }) {
  return (
    <div 
      className={className} 
      style={{
        ...style,
        background: 'var(--lp-bg-alt)',
        borderColor: 'var(--lp-border)',
      }}
    >
      {/* Stars */}
      <div className="text-brand-gz-amber text-xs mb-2 select-none" aria-label={`${review.stars} stars`}>
        ★★★★★
      </div>

      {/* Text */}
      <p 
        className="text-xs leading-relaxed mb-3"
        style={{ color: 'var(--lp-text)' }}
      >
        "{review.text}"
      </p>

      {/* Author */}
      <div 
        className="flex items-center gap-2.5 pt-2.5 border-t"
        style={{ borderColor: 'var(--lp-border)' }}
      >
        <div
          className="w-7 h-7 rounded-full bg-brand-gz-purple/10 text-brand-gz-purple text-[10px] font-semibold flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          {review.initials}
        </div>
        <div>
          <p 
            className="text-xs font-semibold leading-none"
            style={{ color: 'var(--lp-text)' }}
          >
            {review.name}
          </p>
          <p 
            className="text-[10px] mt-0.5"
            style={{ color: 'var(--lp-text-muted)' }}
          >
            {review.role}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ReviewsSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="reviews"
      ref={sectionRef}
      className="py-10 sm:py-24 overflow-hidden transition-colors duration-300"
      style={{ background: 'var(--lp-bg)' }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-xs font-bold text-brand-gz-purple tracking-[0.2em] uppercase mb-4">
            REVIEWS
          </p>
          <h2
            className="font-display-gz font-black leading-tight tracking-tight"
            style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: 'var(--lp-text)' }}
          >
            Gen Z approved.
          </h2>
          <p 
            className="text-sm mt-3 font-medium" 
            style={{ color: 'var(--lp-text-muted)' }}
          >
            Real students, real results, real jobs.
          </p>
        </div>

        {/* ── Desktop: Scattered Layout (md+) ── */}
        <div className="hidden md:block relative" style={{ minHeight: '720px' }}>
          {REVIEWS.map((review, i) => {
            const pos = POSITIONS[i];
            return (
              <ReviewCard
                key={review.name}
                review={review}
                className="absolute w-[300px] rounded-xl p-4 border cursor-default select-none hover:-translate-y-1.5 transition-all duration-300"
                style={{
                  top: pos.top,
                  left: pos.left,
                  transform: `rotate(${pos.rotate}) ${isVisible ? 'scale(1)' : 'scale(0)'}`,
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: pos.delay,
                  transitionDuration: '0.5s',
                  transitionTimingFunction: isVisible ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' : 'ease',
                  transitionProperty: 'opacity, transform',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                }}
              />
            );
          })}
        </div>

        {/* ── Mobile: Grid Fallback ── */}
        <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
          {REVIEWS.map((review) => (
            <ReviewCard
              key={review.name}
              review={review}
              className="rounded-xl p-4 border shadow-sm"
              style={{}}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
